import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Images } from "../../../utils/Images";
import ImageSlider from "./ImageSlider";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to parse description into content array
const closingKeywords = [
  "regards",
  "warm regards",
  "thanks & regards",
  "thanks and regards",
  "best regards",
  "best wishes",
  "sincerely",
  "yours truly",
];

const parseDescription = (description) => {
  if (!description) return [];
  
  const content = [];
  const paragraphs = description.split('\r\n\r\n').filter(p => p.trim());
  let awaitingSignature = false;
  
  paragraphs.forEach((para, index) => {
    if (index === 0 && para.includes('Dear')) {
      // First paragraph might be a title
      content.push({ type: "title", text: para.trim() });
    } else {
      // Regular paragraphs
      const lines = para.split('\r\n').filter(l => l.trim());
      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        const normalized = trimmedLine.replace(/[.,]/g, '').toLowerCase();

        if (closingKeywords.some(keyword => normalized === keyword)) {
          content.push({ type: "closing", text: trimmedLine });
          awaitingSignature = true;
          return;
        }

        if (awaitingSignature) {
          content.push({ type: "signature", text: trimmedLine });
          awaitingSignature = false;
          return;
        }

        content.push({ type: "paragraph", text: trimmedLine });
      });
    }
  });
  
  return content.length > 0 ? content : [{ type: "paragraph", text: description }];
};

const UpdateSection = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isAbove600px = useMediaQuery("(min-width: 600px)");

  const updatesData = useMemo(() => {
    if (Array.isArray(campaignData?.updates) && campaignData.updates.length > 0) {
      return campaignData.updates.map((update, index) => ({
        ...update,
        id: update.id || index + 1,
        date: update.date || update.updatedAt || update.createdAt || "",
        content: Array.isArray(update.content) && update.content.length > 0
          ? update.content
          : parseDescription(update.description || ""),
      }));
    }
    return [];
  }, [campaignData?.updates]);

  // Don't render if no updates
  if (!updatesData || updatesData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        // maxWidth: "1200px",
        mx: "auto",
        p: isMobile ? "0px" : "30px",
        border: isMobile ? "none" : "1px solid #F1F1F1",
        borderRadius: "12px",
        width: "100%",
      }}
    >
      {/* Title */}
      <Typography
        variant="h4"
        component="h2"
        sx={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          fontSize: isMobile ? "14px" : "24px",
          fontWeight: "700",
          mb: isMobile ? "10px" : "20px",
          color: "#1D1D1B",
          borderBottom: "1px solid #E6E8EC",
          paddingBottom: isMobile ? "10px" : "22px",
        }}
      >
        <img
          src={Images.streamlineSharpStar}
          alt="Images.streamlineSharpStar"
          style={{ width: isMobile ? "14px" : "24px", height: isMobile ? "14px" : "24px" }}
        />{" "}
        Updates
      </Typography>

      {/* Render each update */}
      {updatesData.map((update, index) => {
        const updateNumber = index + 1;
        const updateDate =
          update.formattedDate ||
          (update.date && update.date.includes("/") ? update.date : formatDate(update.date)) ||
          "07/08/2025";
        const contentItems =
          Array.isArray(update.content) && update.content.length > 0
            ? update.content
            : parseDescription(update.description || "");

        return (
        <Box
          key={updateNumber}
          sx={{
            mb:
              index < updatesData.length - 1
                ? isMobile
                  ? "12px"
                  : "30px"
                : "0px",
            pb:
              index < updatesData.length - 1
                ? isMobile
                  ? "20px"
                  : "30px"
                : "0px",
            borderBottom:
              index < updatesData.length - 1 ? "1px solid #B9B9B8" : "0px",
          }}
        >
          {/* Update Header */}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: isMobile ? "10px" : "14px",
              fontWeight: 700,
              color: "#151513",
              mb: isMobile ? "14px" : "30px",
              backgroundColor: "#F5F8ED",
              padding: isMobile ? "7px 10px" : "16px",
              borderRadius: isMobile ? "3px" : "8px",
            }}
          >
            <Box
              component="span"
              sx={{
                borderRight: "0.5px solid #B9B9B8",
                paddingRight: "6px",
                marginRight: "6px",
              }}
            >
              #{updateNumber} -
            </Box>
            Updated on{" "}
            <Box component="span" sx={{ color: "#8EA946", fontWeight: "800" }}>
              {updateDate}
            </Box>
          </Typography>

          {/* Content Layout */}
          <Box
          className="update-content"
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "14px" :"20px",
              alignItems: "flex-start",
            }}
          >
             {/* Media Section — images or video, same slot */}
             {(update.images?.length > 0 || update.video_url) && (
               <Box
                 sx={{
                   width: "100%",
                   maxWidth: isAbove600px ? "400px" : "100%",
                   height: isMobile ? "auto" : "385px",
                   "@media (min-width: 1600px)": {
                     height: "340px",
                   },
                   flex: isMobile ? "none" : isAbove600px ? "0 0 400px" : "1",
                   borderRadius: "10px",
                   overflow: "hidden",
                   backgroundColor: update.video_url ? "#000" : "transparent",
                 }}
               >
                {update.video_url ? (
                  <Box
                    component="iframe"
                    src={update.video_url}
                    title={`Update ${index + 1} video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{ width: "100%", height: isMobile ? "220px" : "100%", display: "block" }}
                  />
                ) : (
                  <ImageSlider
                    images={update.images}
                    settings={{
                      dots: true,
                      infinite: true,
                      speed: 3000,
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      autoplay: true,
                      arrows: false,
                      centerMode: isMobile ? true : false,
                      centerPadding: isMobile ? "30px" : "0px",
                    }}
                    imgSx={{
                      width: isMobile ? "280px" : "100%",
                      height: isMobile ? "180px" : "385px",
                      "@media (min-width: 1600px)": {
                        height: "340px",
                      },
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                    noPaddingRight={true}
                  />
                )}
              </Box>
            )}

            {/* Text Content */}
            <Box sx={{ flex: (update.images?.length > 0 || update.video_url) ? "1" : "100%" }}>
              {contentItems.map((item, pIndex) => {
                let styles = {
                  fontSize: isMobile ? "12px" : isLaptop ? "12px" : "14px",
                  lineHeight: "24px",
                  color: "#1D1D1B",
                  mb: isMobile ? "8px" : isLaptop ? "10px" : "14px",
                  fontWeight: isMobile ? "400" : "500",
                };

                if (item.type === "title") {
                  styles = {
                    ...styles,
                    fontWeight: 700,
                    mb: isLaptop ? "8px" : "20px",
                  };
                }
                if (item.type === "closing") {
                  styles = { ...styles, fontWeight: 600, mb: "0" };
                }
                if (item.type === "signature") {
                  styles = { ...styles, fontWeight: 700, mb: "0" };
                }

                return (
                  <Typography key={pIndex} sx={styles}>
                    {item.text}
                  </Typography>
                );
              })}
            </Box>
          </Box>

        </Box>
      )})}
    </Box>
  );
};

export default UpdateSection;
